import {
  intersection,
  map
} from 'lodash';
import { USA_DATE } from '../constants/timeFormat';
import { orderByArray } from '../helpers/order/orderByArray';
import TimezoneService from '../helpers/timezone/timezoneService';
import { vitalsMonitoringOrder } from '../lib/constants/vitalsMonitoringOrder';
import {
  AddOnServiceEnum,
  EnrolledProgramStatusEnum,
  EnrollService,
  PatientEnrolledProgram,
  PatientHiatusStatusEnum,
  Program,
  ProgramCategoryEnum,
  ServiceCategoryEnum,
  UCTask,
  VitalEnumType
} from '../uc-api-sdk';
import { BGTaskService } from './TaskService/BGTask';
import { TaskService } from './TaskService/TaskService';

export class EnrolledProgramService {
  public enrolledProgram?: PatientEnrolledProgram;

  public vitalScheduleFields = [
    'repeat',
    'target',
    'templateDetails.name',
    'templateDetails.description',
    'templateDetails.schedule',
  ];

  constructor(enrolledProgram?: PatientEnrolledProgram) {
    this.enrolledProgram = enrolledProgram;
  }

  public get id() {
    return this.enrolledProgram?.id;
  }

  public get addOnServicesList() {
    return map(
      this.enrolledProgram?.additionalServices,
      ep => ep?.service
    ).filter(v => !!v) as AddOnServiceEnum[];
  }

  public hasAddOnService(service: AddOnServiceEnum) {
    return this.addOnServicesList.includes(service);
  }

  public isEnrolled() {
    return this.enrolledProgram?.enrolledStatus === EnrolledProgramStatusEnum.ENROLLED;
  }

  public getEnrollmentDate() {
    const enrollmentDate = this.enrolledProgram?.enrollmentDate;
    return enrollmentDate
      ? TimezoneService.calcDateTimeDayjs(enrollmentDate || '', undefined).format(USA_DATE)
      : '';
  }

  public isDischarged() {
    return this.enrolledProgram?.enrolledStatus === EnrolledProgramStatusEnum.DISCHARGED;
  }

  public getMonitoringVitals() {
    return this.enrolledProgram?.vitals || [];
  }

  public getMonitoringVitalsInOrder() {
    return orderByArray(
      this.getMonitoringVitals(),
      vitalsMonitoringOrder,
      'type'
    );
  }

  public getVitals() {
    return (this.enrolledProgram?.vitals?.map((v) => v.type)
      || []) as VitalEnumType[];
  }

  public getVitalTask = (vitalType: VitalEnumType) => {
    const task = this.enrolledProgram?.vitals?.find((v) => v.type === vitalType);
    if (task) {
      if (vitalType === VitalEnumType.BG) {
        return new BGTaskService(task as UCTask);
      }
      return new TaskService(task as UCTask);
    }
    return task;
  };

  public hasVital(vital: VitalEnumType) {
    return this.getVitals().includes(vital);
  }

  public getPrograms() {
    return (this.enrolledProgram?.programs?.map((v) => v.category)
      || []) as ProgramCategoryEnum[];
  }

  public getProgramEnrollments() {
    return this.enrolledProgram?.programs || [];
  }

  public static isProgramPending(
    program?: Program,
  ) {
    if (!program || !program.category) return undefined;

    // programs need consent form to be valid
    const dependablePrograms = [
      ProgramCategoryEnum.APCM
    ];

    return (
      dependablePrograms.includes(program.category)
      && !program.startDate
    );
  }

  public hasProgram(program: ProgramCategoryEnum) {
    return this.getPrograms().includes(program);
  }

  public getServices() {
    return this.enrolledProgram?.services || [] as EnrollService[];
  }

  public getServiceCategories() {
    return map(this.getServices(), (s) => s.category) as ServiceCategoryEnum[];
  }

  public hasService(service: ServiceCategoryEnum) {
    return this.getServiceCategories().includes(service);
  }

  public getHiatusDetail() {
    return this.enrolledProgram?.hiatusDetail;
  }

  public isHiatusStatusPaused() {
    if (!this || !this.getHiatusDetail()) {
      return false;
    }
    const hiatusDetail = this.getHiatusDetail();
    return hiatusDetail?.status
    && hiatusDetail?.status !== PatientHiatusStatusEnum.RESUMED;
  }

  public isVitalMonitoringPaused() {
    const hiatusDetail = this.getHiatusDetail();
    return hiatusDetail?.vitalMonitoring?.status
    && hiatusDetail?.vitalMonitoring?.status !== PatientHiatusStatusEnum.RESUMED;
  }

  public isVisitServicePaused() {
    const hiatusDetail = this.getHiatusDetail();
    return hiatusDetail?.visitService?.status
    && hiatusDetail?.visitService?.status !== PatientHiatusStatusEnum.RESUMED;
  }

  public isBillingPaused() {
    const hiatusDetail = this.getHiatusDetail();
    return hiatusDetail?.billingReport?.status
    && hiatusDetail?.billingReport?.status !== PatientHiatusStatusEnum.RESUMED;
  }

  public isPatientUnableToEngage() {
    return this.enrolledProgram?.unableToEngage?.hiatusUnableToEngage;
  }

  public getEngagementDetail() {
    return {
      hiatusUnableToEngage: this.enrolledProgram?.unableToEngage?.hiatusUnableToEngage,
      unableToEngageReason: this.enrolledProgram?.unableToEngage?.unableToEngageReason,
      unableToEngageUpdateTime: this.enrolledProgram?.unableToEngage?.unableToEngageUpdateTime
    };
  }

  public getDischargedDetail() {
    return this.enrolledProgram?.dischargedDetail;
  }

  public getDischargedAtDayjs() {
    const { dischargedAt } = this.getDischargedDetail() || {};
    return dischargedAt
      ? TimezoneService.calcDateTimeDayjs(dischargedAt, undefined)
      : undefined;
  }

  public getStatus() {
    return this.enrolledProgram?.enrolledStatus;
  }

  public isEditPrograms = (newPrograms?: ProgramCategoryEnum[]) => {
    if (!newPrograms) {
      return false;
    }
    return (
      this.getPrograms().length !== newPrograms.length
      || (
        intersection(
          this.getPrograms(),
          newPrograms
        ).length !== newPrograms.length
      )
    );
  };
}
